import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { useEffect, useState, useCallback, useRef } from 'react';

const POLYGON_STYLE = {
    fillColor: '#0284c7',
    fillOpacity: 0.3,
    strokeWeight: 2,
    strokeColor: '#0284c7',
};

// Freehand polygon drawing built on core Maps primitives (Polyline/Polygon).
// Replaces the deprecated google.maps.drawing.DrawingManager: the user presses
// "draw area", drags on the map to sketch a shape, and on release the path is
// closed into a polygon and passed to onPolygonComplete.
export function useDrawingManager(onPolygonComplete) {
    const map = useMap();
    const core = useMapsLibrary('core');
    const maps = useMapsLibrary('maps');

    const [activeOverlay, setActiveOverlay] = useState(null);
    const [isDrawing, setIsDrawing] = useState(false);

    const listenersRef = useRef([]);
    const pathRef = useRef([]);
    const tempPolylineRef = useRef(null);
    const isSketchingRef = useRef(false);
    const activeOverlayRef = useRef(null);
    const onCompleteRef = useRef(onPolygonComplete);
    onCompleteRef.current = onPolygonComplete;

    const removeListeners = useCallback(() => {
        listenersRef.current.forEach(listener => listener.remove());
        listenersRef.current = [];
    }, []);

    const removeTempPolyline = useCallback(() => {
        if (tempPolylineRef.current) {
            tempPolylineRef.current.setMap(null);
            tempPolylineRef.current = null;
        }
        pathRef.current = [];
        isSketchingRef.current = false;
    }, []);

    const stopDrawing = useCallback(() => {
        removeListeners();
        removeTempPolyline();
        setIsDrawing(false);
        if (map) {
            map.setOptions({ draggable: true });
        }
    }, [map, removeListeners, removeTempPolyline]);

    const clearOverlay = useCallback(() => {
        if (activeOverlayRef.current) {
            activeOverlayRef.current.setMap(null);
            activeOverlayRef.current = null;
            setActiveOverlay(null);
        }
        stopDrawing();
    }, [stopDrawing]);

    const startDrawing = useCallback(() => {
        if (!map || !maps) return;

        clearOverlay();
        setIsDrawing(true);
        map.setOptions({ draggable: false });

        const handleDown = (e) => {
            if (!e.latLng) return;
            isSketchingRef.current = true;
            pathRef.current = [e.latLng];
            tempPolylineRef.current = new maps.Polyline({
                map,
                path: pathRef.current,
                strokeColor: POLYGON_STYLE.strokeColor,
                strokeWeight: POLYGON_STYLE.strokeWeight,
                clickable: false,
            });
        };

        const handleMove = (e) => {
            if (!isSketchingRef.current || !e.latLng || !tempPolylineRef.current) return;
            pathRef.current = [...pathRef.current, e.latLng];
            tempPolylineRef.current.setPath(pathRef.current);
        };

        const handleUp = () => {
            if (!isSketchingRef.current) return;
            const path = pathRef.current;

            if (path.length < 3) {
                removeTempPolyline();
                return;
            }

            const polygon = new maps.Polygon({
                map,
                paths: [...path, path[0]],
                clickable: false,
                ...POLYGON_STYLE,
            });

            activeOverlayRef.current = polygon;
            setActiveOverlay(polygon);
            stopDrawing();

            if (onCompleteRef.current) {
                onCompleteRef.current(polygon);
            }
        };

        listenersRef.current = [
            map.addListener('mousedown', handleDown),
            map.addListener('mousemove', handleMove),
            map.addListener('mouseup', handleUp),
        ];
    }, [map, maps, clearOverlay, removeTempPolyline, stopDrawing]);

    // Clean up overlays and listeners on unmount
    useEffect(() => {
        return () => {
            removeListeners();
            if (tempPolylineRef.current) {
                tempPolylineRef.current.setMap(null);
            }
            if (activeOverlayRef.current) {
                activeOverlayRef.current.setMap(null);
            }
        };
    }, [removeListeners]);

    const isReady = Boolean(map && core && maps);

    return {
        startDrawing,
        clearOverlay,
        activeOverlay,
        isDrawing,
        isReady,
    };
}
